import type { ParamMap } from '@angular/router';

/**
 * Filters a navigation carries to the next dashboard / screen, e.g.
 * customer name = Hitachi AND customer spec = NCQSK50EH35.
 *
 * In the URL each one is a `filterKey` / `filterValue` pair, repeated:
 *   ?filterKey=cust_name&filterValue=Hitachi&filterKey=cust_spec_no&filterValue=NCQSK50EH35
 * so a link with ONE filter looks exactly as before and older readers that only
 * look at the first pair keep working. The same key may repeat (several values
 * of a multi-select): readers group them.
 */
export interface NavigationFilterEntry {
  key: string;
  value: string;
}

/** Query params for a navigation's filters (single filter = the old plain strings). */
export function navigationFilterQueryParams(entries: ReadonlyArray<NavigationFilterEntry>): Record<string, string | string[]> {
  const clean = entries
    .map((entry) => ({ key: String(entry.key ?? '').trim(), value: String(entry.value ?? '') }))
    .filter((entry) => entry.key && entry.value.trim() !== '');
  if (!clean.length) return {};
  if (clean.length === 1) return { filterKey: clean[0].key, filterValue: clean[0].value };
  return { filterKey: clean.map((entry) => entry.key), filterValue: clean.map((entry) => entry.value) };
}

/** Every filter pair in the URL, in order (pairs missing a key or value are skipped). */
export function readNavigationFilters(params: ParamMap): NavigationFilterEntry[] {
  const keys = params.getAll('filterKey');
  const values = params.getAll('filterValue');
  const entries: NavigationFilterEntry[] = [];
  keys.forEach((key, index) => {
    const value = values[index];
    if (key && key.trim() && value != null && value !== '') entries.push({ key: key.trim(), value });
  });
  return entries;
}

/** The same filters grouped by key, values in order: { cust_name: ['Hitachi'], … }. */
export function groupNavigationFilters(entries: ReadonlyArray<NavigationFilterEntry>): Map<string, string[]> {
  const grouped = new Map<string, string[]>();
  entries.forEach(({ key, value }) => {
    const values = grouped.get(key) ?? [];
    if (!values.includes(value)) values.push(value);
    grouped.set(key, values);
  });
  return grouped;
}

/** Column names compared loosely: "Customer Name", "customer_name" and "CUSTOMER-NAME" match. */
export function sameFilterColumn(a: string | null | undefined, b: string | null | undefined): boolean {
  const norm = (text: string | null | undefined) => String(text ?? '').toLowerCase().replace(/[\s_\-]+/g, '');
  return !!norm(a) && norm(a) === norm(b);
}
